<template>
  <draggable-item
    :entry="entry"
    :draggable="draggable && !showAmountInput"
    :clone-on-drag="cloneOnDrag"
  >
    <div
      class="fleet-item"
      v-bind:title="name"
      v-show="!dragging"
      :class="{
        'fleet-item-dragging': dragging,
        'glow-blue': highlight,
        opaque: opaque,
        interactable: draggable
      }"
      :style="{ width: itemWidth }"
    >
      <div class="input-area">
        <input
          v-if="!isSpecialShip && allowEditAmount"
          ref="amountInput"
          class="amount"
          type="number"
          step="1"
          min="0"
          :value="amount"
          pattern="[0-9]+"
          @mousedown="stopPropagation"
          @touchstart="stopPropagation"
          @selectstart="stopPropagation"
          @select="stopPropagation"
          @selectionchange="stopPropagation"
          @focus="onAmountFocus"
          @keypress="onAmountKeypress"
          @change="onAmountChange"
          @blur="onAmountBlur"
          :style="{
            color: showAmountInput ? null : 'transparent',
            top: lostAmount != null ? null : '50%'
          }"
          :class="{
            'pointer-events-none': !showAmountInput,
            'select-auto': showAmountInput
          }"
        />
        <span
          v-if="!isSpecialShip"
          class="amount w-full inline-block"
          v-text="!isSpecialist ? amountText : itemType.name"
          :style="{
            display: showAmountInput ? 'none' : null,
            top: lostAmount != null ? null : '50%',
            'text-align': isSpecialist ? 'left' : null,
            'padding-left': isSpecialist ? '3.5em' : null
          }"
          style="height:2em;line-height: 2em;"
          @mousedown="onNamePointerDown"
          @touchstart="onNamePointerDown"
        ></span>
        <button
          v-else-if="isSpecialShip"
          class="special-ship-settings bg-input inline-block"
          v-text="
            $t('components.fleet.special_ship_level', {
              n: this.item.special ? this.item.special.level : 0
            })
          "
          @click="openSpecialShipSettings"
        ></button>
        <span
          v-if="lostAmount != null"
          class="lost-amount w-full inline-block pointer-events-none"
          v-text="lostText || 'hello world!'"
          :style="{
            display: showAmountInput ? 'none' : null,
            color: lostAmount > 0 ? '#FF8888' : null
          }"
          style="height:1.2em;line-height: 1.2em;"
        ></span>
      </div>
      <img
        v-bind:src="picture"
        draggable="false"
        alt=""
        @mousedown="onPicturePointerDown"
        @touchstart="onPicturePointerDown"
      />
      <!--<span class="name" v-text="name"></span>-->
    </div>
  </draggable-item>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { StringHelper } from "@/helpers/string-helper";
import DraggableItem from "@/components/draggable/DraggableItem";
import Vue from "vue";
import Ship from "@/store/model/ship";
import Defense from "@/store/model/defense";
import Research from "@/store/model/research";
import Specialist from "@/store/model/specialist";
import ResourceValue from "@/store/model/resource-value";

export default {
  name: "FleetItem",
  components: { DraggableItem },
  props: {
    entry: {
      type: Object,
      required: true
    },
    draggable: {
      type: Boolean,
      default: false
    },
    cloneOnDrag: {
      type: Boolean,
      default: false
    },
    opaque: {
      type: Boolean,
      default: false
    },
    simulationBus: {
      type: Vue,
      default: null
    },
    itemTypeSelectionBus: {
      type: Vue,
      default: null
    },
    specialShipBus: {
      type: Vue,
      default: null
    },
    itemWidth: {
      type: String,
      default: "10rem"
    },
    allowEditAmount: {
      type: Boolean,
      default: true
    }
  },
  data: () => {
    return {
      item: null,
      amount: 0,
      picture: "",
      name: "",
      isSpecialShip: false,
      isSpecialist: false,
      lostAmount: null,
      showAmountInput: false,
      itemType: null,
      dragging: false,
      interactingWithName: false,
      interactingWithPicthre: false,
      highlight: false
    };
  },
  computed: {
    ...mapGetters("data", [
      "resourceTypes",
      "shipTypes",
      "defenseTypes",
      "researchTypes",
      "specialistTypes"
    ]),
    amountText() {
      return StringHelper.formatNumber(this.amount);
    },
    lostText() {
      return "-" + StringHelper.formatNumber(this.lostAmount);
    }
  },
  created() {
    this.$on("itemdragstart", this.onDragStart);
    this.$on("itemdragend", this.onDragEnd);
    this.updateItemData();
  },
  mounted() {
    this.itemType = this.getItemType(this.item);
    if (this.simulationBus) {
      this.simulationBus.$on("simulate", this.onResimulated);
    }
    if (this.itemTypeSelectionBus) {
      this.itemTypeSelectionBus.$on("show", this.onItemTypeSelectionOpened);
      this.itemTypeSelectionBus.$on("select", this.onItemTypeSelectionClosed);
      this.itemTypeSelectionBus.$on("cancel", this.onItemTypeSelectionClosed);
    }
  },
  destroyed() {
    if (this.simulationBus) {
      this.simulationBus.$off("simulate", this.onResimulated);
    }
    if (this.itemTypeSelectionBus) {
      this.itemTypeSelectionBus.$off("show", this.onItemTypeSelectionOpened);
      this.itemTypeSelectionBus.$off("select", this.onItemTypeSelectionClosed);
      this.itemTypeSelectionBus.$off("cancel", this.onItemTypeSelectionClosed);
    }
  },
  watch: {
    entry() {
      this.updateItemData();
    }
  },
  methods: {
    ...mapActions("combat", ["markFleetsChanged"]),
    getItemType(item) {
      if (item instanceof ResourceValue) {
        return this.resourceTypes[item.typeId];
      }
      if (item instanceof Ship) {
        return this.shipTypes[item.typeId];
      }
      if (item instanceof Defense) {
        return this.defenseTypes[item.typeId];
      }
      if (item instanceof Research) {
        return this.researchTypes[item.typeId];
      }
      if (item instanceof Specialist) {
        return this.specialistTypes[item.typeId];
      }
      return "";
    },
    updateItemData() {
      this.item = this.entry.item;
      this.itemType = this.getItemType(this.item);
      this.picture = this.itemType != null ? this.itemType.image : "";
      this.name = this.itemType != null ? this.itemType.name : "";
      this.isSpecialShip =
        this.item instanceof Ship &&
        this.itemType != null &&
        this.itemType.flags.indexOf("special") >= 0;
      this.isSpecialist = this.item instanceof Specialist;
      if (this.item instanceof Research) {
        this.amount = this.item.level;
        this.lostAmount = null;
      } else if (this.item instanceof Specialist) {
        this.amount = null;
        this.lostAmount = null;
      } else {
        this.amount = this.item.amount;
        this.lostAmount = this.item.lostAmount;
      }
    },
    stopPropagation(event) {
      event.stopPropagation();
    },
    openSpecialShipSettings(event) {
      event.stopPropagation();
      this.specialShipBus.$emit("show", {
        item: this.item,
        posY: 0
      });
    },
    onResimulated() {
      this.updateItemData();
    },
    onAmountFocus(event) {
      event.stopPropagation();
      this.showAmountInput = true;
    },
    onAmountKeypress(event) {
      if (event.keyCode !== 13) {
        return;
      }
      event.target.blur();
    },
    onAmountChange(event) {
      let amount = parseInt(event.target.value);
      if (isNaN(amount)) {
        amount = 0;
      }
      if (this.item instanceof Research) {
        this.item.level = amount;
      } else {
        this.item.amount = amount;
      }
      this.amount = amount;
    },
    onAmountBlur() {
      this.showAmountInput = false;
      this.markFleetsChanged();
    },
    onDragStart(event) {
      this.$parent.$emit("itemdragstart", event);
      this.dragging = true;
      this.interactingWithName = false;
      this.interactingWithPicture = false;
    },
    onDragEnd(event) {
      this.$parent.$emit("itemdragend", event);
      this.dragging = false;
      this.$el.offsetHeight; // forces css to skip transitions
    },
    onItemTypeSelectionOpened(event) {
      this.highlight = event.entry === this.entry;
    },
    onItemTypeSelectionClosed() {
      if (this.highlight) {
        this.highlight = false;
      }
    },
    onNamePointerDown() {
      if (this.isSpecialist) {
        return;
      }
      this.interactingWithName = true;
      this.hookPointerListeners();
    },
    onPicturePointerDown() {
      this.interactingWithPicture = true;
      this.hookPointerListeners();
    },
    onGlobalPointerUp(event) {
      this.releasePointerHooks();
      if (this.interactingWithName && this.allowEditAmount) {
        this.interactingWithName = false;
        this.$refs["amountInput"].focus();
        this.$refs["amountInput"].select();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (this.interactingWithPicture) {
        this.interactingWithPicture = false;
        event.preventDefault();
        event.stopPropagation();
        this.$emit("pictureclick");
      }
    },
    hookPointerListeners() {
      document.addEventListener("mouseup", this.onGlobalPointerUp);
      document.addEventListener("touchend", this.onGlobalPointerUp);
      document.addEventListener("touchcancel", this.onGlobalPointerUp);
    },
    releasePointerHooks() {
      document.addEventListener("mouseup", this.onGlobalPointerUp);
      document.addEventListener("touchend", this.onGlobalPointerUp);
      document.addEventListener("touchcancel", this.onGlobalPointerUp);
    }
  }
};
</script>

<style scoped>
.fleet-item {
  position: relative;
  box-sizing: border-box;
  height: 3rem;
  border-radius: 0.35rem;
}
#app.mouse .fleet-item.interactable:hover {
  cursor: pointer;
  background-color: var(--color-button-hover);
}
#app.mouse .fleet-item:hover input:not(:focus) {
  cursor: pointer;
}
#app.mouse .fleet-item.opaque {
  background-color: var(--color-button-normal);
}
#app.mouse .fleet-item-dragging.opaque {
  box-shadow: rgba(0, 0, 0, 0.4) 0 -0.1rem 0.5rem;
}
.fleet-item img {
  position: absolute;
  top: 0.1rem;
  left: 0.1rem;
  width: 2.8rem;
  height: 2.8rem;
  border: none;
  border-radius: 0.3rem;
}
.fleet-item .input-area {
  flex: 1;
  height: 3rem;
}
.fleet-item .amount,
.fleet-item .lost-amount {
  background: transparent;
  width: 100%;
}
.fleet-item .amount,
.fleet-item .lost-amount,
.fleet-item .special-ship-settings {
  display: block;
  position: absolute;
  padding-top: 0;
  padding-bottom: 0;
  right: 0;
  text-align: right;
  vertical-align: top;
  border: none;
  box-sizing: border-box;
  padding-right: 0.5rem;
  font-size: 1rem;
  font-family: "Zekton-Condensed", Verdana, sans-serif;
}
.fleet-item .amount {
  top: 30%;
  transform: translateY(-50%);
}
.fleet-item .lost-amount {
  top: 70%;
  transform: translateY(-50%);
}
.fleet-item input.amount {
  height: 2rem;
}
.fleet-item .special-ship-settings {
  height: 1.5rem;
}
#app.mouse .fleet-item .special-ship-settings:hover {
  background-color: var(--color-button-hover);
}
#app.mouse .fleet-item .special-ship-settings:active {
  background-color: var(--color-button-active);
}
#app.mouse .fleet-item input.amount:hover {
  cursor: text;
}
</style>
